import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ArchiveLine,
  CheckmarkCircleFill,
  CheckmarkLine,
  CircleStandardFilledFill,
  CircleStandardLine,
  FailFill,
  ZoomOutFill,
} from '@dropbox/dig-icons/assets';

type StatusButtonLabel = 'no_status' | 'open' | 'closed';

interface StatusOption {
  value: StatusButtonType;
  color: string;
  icon: React.FC;
}

interface StatusButtonOption {
  label: StatusButtonLabel;
  statusOptions: StatusOption[];
}

export const statusButtonTypes = [
  'no_status',
  'not_started',
  'on_track',
  'at_risk',
  'off_track',
  'complete',
  'cancelled',
  'missed',
  'archived',
  'closed',
] as const;

export const isOpenStatus = (status: string): boolean => {
  return ['on_track', 'at_risk', 'off_track'].includes(status);
};

export const isClosedStatus = (status: string): boolean => {
  return ['complete', 'cancelled', 'missed', 'archived', 'closed'].includes(status);
};

type StatusButtonType = (typeof statusButtonTypes)[number];

export const getStatusOptions = (includeArchived?: boolean): StatusButtonOption[] => {
  const closedStatuses: StatusOption[] = [
    {value: 'complete', color: '#5BA250', icon: CheckmarkCircleFill},
    {value: 'cancelled', color: '#6A645F', icon: ZoomOutFill},
    {value: 'missed', color: '#B23A37', icon: FailFill},
  ];
  if (includeArchived) {
    closedStatuses.push({value: 'archived', color: '#6A645F', icon: ArchiveLine});
  }

  return [
    {
      label: 'no_status',
      statusOptions: [{value: 'no_status', color: '#6A645F', icon: CircleStandardFilledFill}],
    },
    {
      label: 'open',
      statusOptions: [
        {value: 'on_track', color: '#5BA250', icon: CircleStandardFilledFill},
        {value: 'at_risk', color: '#D7B21E', icon: CircleStandardFilledFill},
        {value: 'off_track', color: '#B23A37', icon: CircleStandardFilledFill},
      ],
    },
    {
      label: 'closed',
      statusOptions: closedStatuses,
    },
  ];
};

export const getStatusStyle = (status?: string, includeArchived?: boolean): StatusOption => {
  if (status === 'not_started') {
    return {
      value: 'not_started',
      color: '#6A645F',
      icon: CircleStandardLine,
    };
  }

  if (status === 'closed') {
    return {
      value: 'closed',
      color: '#6A645F',
      icon: CircleStandardFilledFill,
    };
  }

  const noStatus: StatusOption = {
    value: 'no_status',
    color: '#6A645F',
    icon: CircleStandardFilledFill,
  };
  const statusOptions = getStatusOptions(includeArchived);
  return status
    ? statusOptions
        .find((option) => option.statusOptions.some((item) => item.value === status))
        ?.statusOptions.find((item) => item.value === status) || noStatus
    : noStatus;
};

export const StatusButtonIcon = ({
  size = 'standard',
  status,
  substatus,
  completeNew,
  includeArchived,
  statusColor,
}: {
  size?: 'xsmall' | 'small' | 'standard';
  status?: string;
  substatus?: string;
  completeNew?: boolean;
  includeArchived?: boolean;
  statusColor?: string;
}) => {
  const statusStyle = getStatusStyle(status, includeArchived);
  const subStatusStyle = substatus ? getStatusStyle(substatus, includeArchived) : undefined;
  let iconColor = statusColor ?? statusStyle.color;

  if (completeNew && statusStyle.value === 'complete') {
    statusStyle.icon = CheckmarkLine;
    iconColor = 'var(--dig-color__text__subtle)';
  }

  return (
    <Box display="flex" alignItems="center">
      <UIIcon
        size={size === 'xsmall' ? 'small' : size}
        src={statusStyle.icon}
        color={iconColor}
        style={{
          width: size === 'xsmall' ? 12 : undefined,
          height: size === 'xsmall' ? 12 : undefined,
        }}
      />

      {subStatusStyle && statusStyle.value !== subStatusStyle?.value && (
        <>
          <UIIcon
            src={CircleStandardFilledFill}
            size="small"
            style={{width: 14, marginLeft: -9, marginTop: 10}}
            className={atoms({color: 'Background Base'})}
          />
          <UIIcon
            src={subStatusStyle.icon}
            color={subStatusStyle.color}
            size="small"
            style={{width: 10, marginLeft: -12, marginTop: 10}}
          />
        </>
      )}
    </Box>
  );
};
